import { apiFetch } from "./client";
import type { Language, Page, TranslationRow } from "./types";

export type { Language, Page, TranslationRow };

export async function listLanguages(): Promise<Language[]> {
  return apiFetch<Language[]>("/languages");
}

export async function createLanguage(data: LanguageInput): Promise<Language> {
  return apiFetch<Language>("/languages", { method: "POST", body: JSON.stringify(data) });
}

export async function updateLanguage(code: string, data: LanguageInput): Promise<Language> {
  return apiFetch<Language>(`/languages/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export interface LanguageInput {
  code: string;
  display_name: string;
  native_name: string;
  direction: "ltr" | "rtl";
  is_active: boolean;
  is_primary: boolean;
  sort_order: number;
}

export interface TranslationQuery {
  q?: string;
  namespace?: string;
  machine_only?: boolean;
  limit?: number;
  offset?: number;
}

export async function listTranslationNamespaces(): Promise<string[]> {
  return apiFetch<string[]>("/translations/namespaces");
}

export async function listTranslations(
  query: TranslationQuery = {},
): Promise<Page<TranslationRow>> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  if (query.namespace) params.set("namespace", query.namespace);
  if (query.machine_only) params.set("machine_only", "true");
  params.set("limit", String(query.limit ?? 50));
  params.set("offset", String(query.offset ?? 0));
  return apiFetch<Page<TranslationRow>>(`/translations?${params.toString()}`);
}

export interface TranslationKeyInput {
  key: string;
  namespace: string;
  description?: string | null;
  max_length_hint?: number | null;
}

export async function createTranslationKey(data: TranslationKeyInput): Promise<unknown> {
  return apiFetch("/translations", { method: "POST", body: JSON.stringify(data) });
}

export async function updateTranslationKey(
  key: string,
  data: TranslationKeyInput,
): Promise<unknown> {
  return apiFetch(`/translations/keys/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteTranslationKey(key: string): Promise<unknown> {
  return apiFetch(`/translations/keys/${encodeURIComponent(key)}`, { method: "DELETE" });
}

export async function upsertTranslation(
  key: string,
  data: { language_code: string; value: string; is_machine_translated?: boolean },
): Promise<void> {
  return apiFetch<void>(`/translations/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function machineTranslateField(
  key: string,
  target_language: string,
): Promise<{ key: string; value: string }> {
  return apiFetch<{ key: string; value: string }>(
    `/translations/${encodeURIComponent(key)}/machine-translate`,
    { method: "POST", body: JSON.stringify({ key, target_language }) },
  );
}

export async function machineTranslateSection(
  namespace: string,
  target_language?: string,
): Promise<{ namespace: string; translated: number; values: Record<string, string> }> {
  return apiFetch<{ namespace: string; translated: number; values: Record<string, string> }>(
    `/translations/sections/${encodeURIComponent(namespace)}/machine-translate`,
    { method: "POST", body: JSON.stringify({ namespace, target_language: target_language ?? null }) },
  );
}

export async function publishTranslations(): Promise<{
  locales: string[];
  version: string;
  total_keys: number;
}> {
  return apiFetch<{ locales: string[]; version: string; total_keys: number }>(
    "/translations/publish",
    { method: "POST" },
  );
}

/** Fetch a published bundle without auth (public endpoint for the SPA). */
export async function fetchBundle(locale: string): Promise<Record<string, unknown>> {
  const response = await fetch(`/api/v1/translations/bundle/${encodeURIComponent(locale)}`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Failed to load locale bundle: ${response.status}`);
  }
  return (await response.json()) as Record<string, unknown>;
}
